import React, { useContext } from 'react';
import { Navigate, useParams, useLocation, Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppContext } from '../../context';
import BlockLoading from '../BlockLoading';

const mapKebabToCamel = (kebabStr) => {
	return kebabStr.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

const CategoryAccessGuard = ({ category }) => {
	const {
		session: { status, menuConfig, testTypes: testTypesMap },
	} = useContext(AppContext);
	const { kategori, subkategori } = useParams();
	const location = useLocation();

	// If still checking authentication, wait.
	if (status === 'checking') {
		return (
			<div
				style={{
					width: '100vw',
					height: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<BlockLoading />
			</div>
		);
	}

	// If not authenticated, let the MainLayout session guard handle redirect
	if (status !== 'authenticated') {
		return <Outlet />;
	}

	// Wait for configuration to load
	if (!menuConfig || !testTypesMap) {
		return (
			<div
				style={{
					width: '100vw',
					height: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<BlockLoading />
			</div>
		);
	}

	// 1. Check parent category existence and access
	const allowedCategoryIds = menuConfig[category];
	if (!allowedCategoryIds || !Array.isArray(allowedCategoryIds) || allowedCategoryIds.length === 0) {
		return <Navigate to="/beranda" replace />;
	}

	// 2. Check kecermatan specific routes
	const isKecermatanRoute = location.pathname.split('/').includes('kecermatan');
	if (isKecermatanRoute) {
		// Kecermatan (CRM) must be allowed under psikologi
		if (!allowedCategoryIds.includes('CRM')) {
			return <Navigate to="/beranda" replace />;
		}

		// If subcategory is present, check against crmAllowedSubKategori
		if (subkategori) {
			const subCategoryCamel = mapKebabToCamel(subkategori);
			const allowedSubCategories = menuConfig.crmAllowedSubKategori || [];
			const isAllowedSubCategory = allowedSubCategories.some(
				(subCategoryId) =>
					subCategoryId === subCategoryCamel ||
					testTypesMap?.[subCategoryId]?.path === subkategori
			);

			if (!isAllowedSubCategory) {
				return <Navigate to="/beranda" replace />;
			}
		}
	}

	// 3. Check specific test type (kategori param)
	if (kategori && kategori !== 'kecermatan') {
		const testType = Object.values(testTypesMap || {}).find(
			(t) => t.path === kategori && t.category === category
		);

		// If the test type exists, check if user is allowed to access it
		if (testType) {
			if (!allowedCategoryIds.includes(testType.id)) {
				return <Navigate to="/beranda" replace />;
			}
		}
	}

	return <Outlet />;
};

CategoryAccessGuard.propTypes = {
	category: PropTypes.oneOf(['akademik', 'psikologi', 'jasmani']).isRequired,
};

export default CategoryAccessGuard;
